import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TenantContext = createContext();

export function TenantProvider({ children }) {
  const { user } = useAuth();
  const [tenant, setTenant] = useState(null);
  const [features, setFeatures] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchTenantInfo();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchTenantInfo = async () => {
    try {
      const response = await axios.get(`${API}/tenants/my`);
      setTenant(response.data.tenant);
      setFeatures(response.data.features);
      setIsSuperAdmin(response.data.is_super_admin);
    } catch (error) {
      console.error('Error fetching tenant info:', error);
      // Default features for error case
      setFeatures({
        magazzino: false,
        statistiche: false,
        export_pdf: true,
        export_excel: true,
        backup: false,
        listino_prezzi: false,
        calendario: true,
        voice_input: false,
        notifiche_scorta: false,
        zone: false,
      });
    } finally {
      setLoading(false);
    }
  };

  // Helper function to check if a feature is enabled
  const hasFeature = (featureName) => {
    if (isSuperAdmin) return true;
    return features?.[featureName] ?? false;
  };

  // Get piano name
  const getPianoName = () => {
    if (isSuperAdmin) return 'Super Admin';
    return tenant?.piano?.charAt(0).toUpperCase() + tenant?.piano?.slice(1) || 'Base';
  };

  return (
    <TenantContext.Provider value={{
      tenant,
      features,
      isSuperAdmin,
      loading,
      hasFeature,
      getPianoName,
      refreshTenant: fetchTenantInfo
    }}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within TenantProvider');
  }
  return context;
}
